/**
 * Vanilla Customize Modal
 *
 * Centered dialog with a backdrop, focus trap, Escape-to-close, and a
 * focus restore on unmount. Renders category rows via
 * `categoryToggles.ts`. No Flowbite/React dependency - styled with
 * Tailwind utility classes to match the SPA visuals.
 *
 * Opened by `core.showCustomize()` from the banner or from the Devise-
 * layout re-access link. Closed by Save (persists) or Cancel/Escape
 * (dismisses without saving).
 *
 * Categories are snapshotted from `getState()` at open. Under Global
 * Privacy Control the Advertising switch is locked off and both Accept All
 * and Save send `advertising: false`.
 */

import { getState, hideCustomize, save, type ConsentCategories } from "./core";
import {
  renderCategoryToggles,
  type CategoryToggleHandle,
} from "./categoryToggles";

const MODAL_ELEMENT_ID = "as-customize-modal";

export interface CustomizeModalMountOptions {
  privacyPolicyHref?: string;
}

export interface CustomizeModalHandle {
  unmount: () => void;
}

/**
 * Mount the modal into the given container. Calling twice is a no-op.
 *
 * @param container - usually `document.body`; defaults as such
 * @param options - optional overrides (privacy policy URL)
 * @returns handle exposing `unmount()` so the entrypoint can remove the
 *   modal when state flips `customizeOpen = false`
 */
export function mountCustomizeModal(
  container: HTMLElement = document.body,
  options: CustomizeModalMountOptions = {},
): CustomizeModalHandle {
  const existing = document.getElementById(MODAL_ELEMENT_ID);
  if (existing) {
    return { unmount: () => existing.remove() };
  }

  const privacyHref = options.privacyPolicyHref ?? "/privacy-policy";
  const triggerBeforeOpen =
    (document.activeElement as HTMLElement | null) ?? null;

  const snapshot = getState();
  const gpcLocked = snapshot.gpcActive;
  const initialCategories: ConsentCategories = {
    necessary: true,
    analytics: snapshot.categories.analytics === true,
    advertising: !gpcLocked && snapshot.categories.advertising === true,
  };
  let workingCategories: ConsentCategories = { ...initialCategories };

  const backdrop = document.createElement("div");
  backdrop.id = MODAL_ELEMENT_ID;
  backdrop.className =
    "fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-overlay-scrim motion-safe:animate-[fadeIn_0.15s_ease-out]";
  backdrop.setAttribute("role", "presentation");

  const dialog = document.createElement("div");
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-labelledby", `${MODAL_ELEMENT_ID}-title`);
  dialog.className =
    "w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[85vh] overflow-hidden bg-surface-card sm:rounded-xl shadow-2xl flex flex-col";
  backdrop.appendChild(dialog);

  const header = document.createElement("header");
  header.className =
    "flex items-start justify-between gap-3 px-5 py-4 border-b border-border-base";
  const title = document.createElement("h2");
  title.id = `${MODAL_ELEMENT_ID}-title`;
  title.className = "text-lg font-semibold text-text-title";
  title.textContent = "Cookie Preferences";
  header.appendChild(title);
  dialog.appendChild(header);

  const body = document.createElement("div");
  body.className = "flex-1 overflow-y-auto px-5 py-4";
  const intro = document.createElement("p");
  intro.className = "text-sm text-text-label mb-4";
  intro.innerHTML =
    "We use the cookies listed below. Strictly Necessary cookies are always on. " +
    'Read our <a class="font-medium text-text-teal-700 underline hover:text-text-teal-800" href="' +
    privacyHref +
    '">Privacy Policy</a> for full details.';
  body.appendChild(intro);

  const togglesContainer = document.createElement("div");
  body.appendChild(togglesContainer);
  dialog.appendChild(body);

  let handle: CategoryToggleHandle | null = renderCategoryToggles(
    togglesContainer,
    {
      values: initialCategories,
      gpcLocked,
      onChange: (next) => {
        workingCategories = next;
      },
    },
  );

  const footer = document.createElement("footer");
  footer.className =
    "flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 px-5 py-4 border-t border-border-base bg-surface-alt";
  dialog.appendChild(footer);

  const cancelBtn = buildFooterButton({
    label: "Cancel",
    variant: "secondary",
  });
  const acceptBtn = buildFooterButton({
    label: "Accept All",
    variant: "secondary",
  });
  const saveBtn = buildFooterButton({
    label: "Save Preferences",
    variant: "primary",
  });
  footer.appendChild(cancelBtn);
  footer.appendChild(acceptBtn);
  footer.appendChild(saveBtn);

  const close = () => {
    handle?.destroy();
    handle = null;
    backdrop.remove();
    document.removeEventListener("keydown", onKeydown, true);
    if (triggerBeforeOpen && typeof triggerBeforeOpen.focus === "function") {
      triggerBeforeOpen.focus();
    }
  };

  cancelBtn.addEventListener("click", () => {
    hideCustomize();
  });
  acceptBtn.addEventListener("click", () => {
    void save(
      { necessary: true, analytics: true, advertising: !gpcLocked },
      "banner_customize",
    );
  });
  saveBtn.addEventListener("click", () => {
    void save(
      {
        necessary: true,
        analytics: workingCategories.analytics === true,
        advertising: !gpcLocked && workingCategories.advertising === true,
      },
      "banner_customize",
    );
  });
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) hideCustomize();
  });

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      hideCustomize();
      return;
    }
    if (e.key === "Tab") {
      trapFocus(e, dialog);
    }
  };
  document.addEventListener("keydown", onKeydown, true);

  container.appendChild(backdrop);

  // Focus the first focusable element inside the dialog.
  const firstFocusable = getFocusable(dialog)[0];
  firstFocusable?.focus();

  return { unmount: close };
}

interface FooterButtonOptions {
  label: string;
  variant: "primary" | "secondary";
}

/**
 * Build one of the three footer buttons (Cancel / Accept All / Save).
 *
 * @param opts - label + variant
 * @returns the styled button element
 */
function buildFooterButton(opts: FooterButtonOptions): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = opts.label;
  const base =
    "inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium " +
    "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-fixed-teal-500 focus-visible:ring-offset-surface-card " +
    "w-full sm:w-auto";
  btn.className =
    base +
    (opts.variant === "primary"
      ? " bg-fixed-teal-600 text-white hover:bg-fixed-teal-700"
      : " border border-border-control bg-button-neutral-bg text-text-emphasis hover:bg-surface-alt");
  return btn;
}

/**
 * Return the focusable descendants of the dialog in tab order.
 *
 * @param dialog - the dialog root
 * @returns an array of focusable elements
 */
function getFocusable(dialog: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), [role="switch"]:not([disabled]), summary, input:not([disabled]), ' +
    '[tabindex]:not([tabindex="-1"])';
  return Array.from(dialog.querySelectorAll<HTMLElement>(selector)).filter(
    (el) =>
      !el.hasAttribute("aria-disabled") ||
      el.getAttribute("aria-disabled") === "false",
  );
}

/**
 * Cycle focus within the dialog when the user tabs past the first/last
 * focusable descendant. Prevents keyboard users from escaping the modal
 * into the page behind it.
 *
 * @param e - the `keydown` event (Tab)
 * @param dialog - the dialog root
 */
function trapFocus(e: KeyboardEvent, dialog: HTMLElement): void {
  const focusables = getFocusable(dialog);
  if (focusables.length === 0) return;

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (e.shiftKey && (active === first || !dialog.contains(active))) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}
